import { useQuery } from '@tanstack/react-query'

import config from '@config'

const ALLOWED_TYPES_OF_SPORT = ['soccer', 'hockey'] as const

export type AllowedTypeOfSport = (typeof ALLOWED_TYPES_OF_SPORT)[number]
interface UseRecentMatchOutcomesProps {
  typeOfSport: 'soccer'
  matchId?: string
  competitorId?: string
}

export interface RecentMatchOutcomesTeam {
  abbreviation: string
  recentMatchOutcomes: RecentMatchOutcome[]
}

export interface RecentMatchOutcomesOpponent {
  id: string
  name: string
  abbreviation: string
  image: {
    src: string
    alt: string
  }
}

export type RecentMatchOutcome = {
  matchId: string
  opponent: RecentMatchOutcomesOpponent
  type: 'win' | 'loss' | 'draw'
}

export type RecentMatchOutcomesAPIResponse =
  | RecentMatchOutcomesTeam
  | {
      homeTeam: RecentMatchOutcomesTeam
      awayTeam: RecentMatchOutcomesTeam
    }

const {
  sports: {
    baseUrlV2: sportsBaseUrlV2,
    recentMatchOutcomesUrlTemplateMatchV2,
    recentMatchOutcomesUrlTemplateCompetitorV2,
  },
} = config

export const validMatchId = (matchId: UseRecentMatchOutcomesProps['matchId']) =>
  !!matchId && matchId !== '0'

export const validCompetitorId = (
  competitorId: UseRecentMatchOutcomesProps['competitorId'],
) => !!competitorId && competitorId !== '0'

export const validTypeOfSport = (
  typeOfSport: UseRecentMatchOutcomesProps['typeOfSport'],
) => ALLOWED_TYPES_OF_SPORT.includes(typeOfSport)

const fetchRecentMatchOutcomesData = async ({
  typeOfSport,
  matchId,
  competitorId,
}: UseRecentMatchOutcomesProps) => {
  const requestUrl = `${sportsBaseUrlV2}${(competitorId
    ? recentMatchOutcomesUrlTemplateCompetitorV2
    : recentMatchOutcomesUrlTemplateMatchV2
  )
    .replace('{SPORT}', typeOfSport)
    .replace('{API_VERSION}', 'v0')
    .replace(
      `${competitorId ? '{COMPETITOR_ID}' : '{MATCH_ID}'}`,
      `${competitorId ?? matchId}`,
    )}`

  const response = await fetch(requestUrl)

  if (!response.ok) {
    throw new Error('Error fetching recent match outcomes.')
  }

  return response.json() as Promise<RecentMatchOutcomesAPIResponse>
}

const useRecentMatchOutcomes = ({
  matchId,
  typeOfSport,
  competitorId,
}: UseRecentMatchOutcomesProps) => {
  const isEnabled =
    (validMatchId(matchId) || validCompetitorId(competitorId)) &&
    validTypeOfSport(typeOfSport)

  return useQuery({
    queryKey: [
      'sportsRecentMatchOutcomes',
      typeOfSport,
      !!competitorId ? 'competitor' : 'match',
      competitorId ?? matchId,
    ],
    queryFn: () =>
      fetchRecentMatchOutcomesData({
        typeOfSport,
        matchId,
        competitorId,
      }),
    enabled: isEnabled,
  })
}

export default useRecentMatchOutcomes
